import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { APP_NAME } from '@amzjp/shared';
import { menuBaglantisiTiklandi, NAV_GROUPS, NAVIGATION, type NavSection } from './navigation';
import { MarkaYazisi, UlkeRozeti } from './marka';
import { cn } from '@/lib/utils';

/** Bu fazda henüz gelmemiş ekranlar sönük gösterilir — kullanıcı boş sayfaya tıklamasın. */
/**
 * Tamamlanmış son faz. Menüde bundan sonraki fazlara ait bağlantılar
 * "hazır değil" olarak gösteriliyor.
 */
const CURRENT_PHASE = 9;

export function Sidebar({ acik = false, kapat }: { acik?: boolean; kapat?: () => void } = {}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      NAVIGATION.filter((s) => s.children?.some((c) => pathname.startsWith(c.to))).map((s) => [
        s.label,
        true,
      ]),
    ),
  );

  return (
    /*
     * ⚠️ Mobil kuralların hepsi `max-lg:` önekli.
     *
     * Tailwind'de `max-lg:` yalnızca `@media (max-width: 1023px)` içine kural
     * yazıyor. Yani 1024px ve üstünde bu satırların hiçbiri uygulanmıyor ve
     * menü tam olarak eskisi gibi — akışta, 240px, sabit. Masaüstünün
     * bozulmadığı bir söz değil, CSS'in matematiği.
     *
     * Mobilde ise ekranın dışında duruyor (`-translate-x-full`) ve üstteki
     * menü düğmesiyle içeri kayıyor.
     */
    <aside
      className={cn(
        // 28 Eyl 2026: menü üste taşındı; bu bileşen artık yalnızca dar ekranda çekmece
        'fixed inset-y-0 left-0 z-50 flex h-full w-[16rem] shrink-0 flex-col border-r border-border bg-bg-elevated',
        'transition-transform duration-200',
        acik ? 'translate-x-0 shadow-[var(--shadow-md)]' : '-translate-x-full',
      )}
      /*
       * ⚠️ Bunlar YALNIZCA mobilde ve kapalıyken verilmeli.
       *
       * `acik` masaüstünde her zaman `true` geliyor (AppShell ekran
       * genişliğine bakıyor). Koşulsuz yazılsaydı masaüstünde menü
       * klavyeyle gezilemez ve ekran okuyucuya görünmez olurdu — göze
       * çarpmayan ama ağır bir erişilebilirlik hatası.
       */
      aria-hidden={acik ? undefined : 'true'}
      /*
       * `inert` React 18 tiplerinde yok (React 19'da eklendi), bu yüzden
       * yayılımla veriliyor. Tarayıcı niteliği tanıyor: kapalı çekmecedeki
       * bağlantılar sekme sırasından çıkıyor. Olmasa, ekran dışındaki menüde
       * Tab'a basan kullanıcı görünmez bağlantılarda geziniyordu.
       */
      {...({ inert: acik ? undefined : '' } as Record<string, unknown>)}
    >
      <div className="flex h-[var(--nav-h)] items-center gap-2.5 px-4">
        <img
          src="/logo.svg"
          alt=""
          className="size-7 rounded-[var(--radius-sm)]"
          aria-hidden="true"
        />
        <MarkaYazisi />
        <span className="ml-auto">
          <UlkeRozeti />
        </span>
      </div>

      <nav
        className="flex-1 overflow-y-auto px-2 pb-2"
        aria-label="Ana menü"
        // Mobilde bir bağlantıya basınca çekmece kapansın; masaüstünde
        // `kapat` verilmediği için bu bir şey yapmıyor.
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a')) kapat?.();
        }}
      >
        {NAV_GROUPS.map((grup) => {
          const bolumler = NAVIGATION.filter((b) => (b.group ?? 'islem') === grup.id);
          if (bolumler.length === 0) return null;

          return (
            <div key={grup.id} className="mb-5 last:mb-0">
              {/*
                Küme başlığı yalnızca bir AYRAÇ; tıklanabilir değil ve sekme
                sırasında yer almıyor. Gözün listeyi üç parçaya bölmesini
                sağlıyor — aradığı şeyin hangi parçada olduğunu bilen kullanıcı
                diğer ikisini hiç taramıyor.
              */}
              <p className="px-1 pb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
                {grup.label}
              </p>
              <ul className="space-y-1.5">
                {bolumler.map((section) => (
                  <NavSectionItem
                    key={section.label}
                    section={section}
                    pathname={pathname}
                    isOpen={open[section.label] ?? false}
                    toggle={() =>
                      setOpen((prev) => ({
                        ...prev,
                        [section.label]: !(prev[section.label] ?? false),
                      }))
                    }
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/*
        Burada "Faz 9 · Sertleştirme ve Yayın" yazıyordu ve 404 dâhil HER
        ekranda görünüyordu. Geliştirme takibi için konmuş bir etiketti
        (Ağustos 2026); markalı bir üründe kendi logosunun altında mühendislik
        terimi taşımak, ürünün bitmemiş olduğu izlenimi veriyor.
      */}
      <div className="px-4 py-3">
        <p className="text-[0.625rem] text-fg-subtle">{APP_NAME} · Sürüm 1.0</p>
      </div>
    </aside>
  );
}

/** Tek bir üst düzey bölüm: doğrudan bağlantı ya da açılır alt liste. */
function NavSectionItem({
  section,
  pathname,
  isOpen,
  toggle,
}: {
  section: NavSection;
  pathname: string;
  isOpen: boolean;
  toggle: () => void;
}) {
  const Icon = section.icon;
  const isActive = pathname === section.to || section.children?.some((c) => pathname === c.to);

  if (!section.children) {
    return (
      <li>
        <NavLink to={section.to} active={pathname === section.to} phase={section.phase} ustDuzey>
          <Icon
            className={cn(
              'size-[1.125rem] shrink-0',
              pathname === section.to ? 'text-accent' : 'text-fg-muted',
            )}
            aria-hidden="true"
          />
          {section.label}
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        /*
         * Üst düzey bölümler kendi YÜZEYİNE sahip. Eskiden çıplak satırlardı ve
         * menü "boşlukta duran bir kelime listesi" gibi görünüyordu — kullanıcının
         * "çok basit ve küçük duruyor" dediği şey buydu. Kenarlıklı bir zemin,
         * her bölümü tıklanabilir bir hedef olarak okutuyor.
         */
        className={cn(
          'flex w-full items-center gap-2.5 rounded-[var(--radius)] border px-3 py-2.5',
          'text-sm transition-colors',
          isActive
            ? 'border-accent/30 bg-accent-soft font-semibold text-accent'
            : 'border-border bg-bg-elevated text-fg hover:border-border-strong hover:bg-bg-sunken',
        )}
      >
        <Icon
          className={cn('size-[1.125rem] shrink-0', isActive ? 'text-accent' : 'text-fg-muted')}
          aria-hidden="true"
        />
        <span className="flex-1 text-left tracking-[-0.01em]">{section.label}</span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 transition-transform',
            isActive ? 'text-accent' : 'text-fg-subtle',
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <ul className="menu-acilis mt-1.5 mb-1 ml-4 space-y-px border-l border-border pl-3">
          {section.children.map((child) => (
            <li key={child.to + child.label}>
              <NavLink to={child.to} active={pathname === child.to} phase={child.phase}>
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function NavLink({
  to,
  active,
  phase,
  ustDuzey = false,
  children,
}: {
  to: string;
  active: boolean;
  phase?: number;
  /** Üst düzey bölümler kendi yüzeyine sahip; alt maddeler girintili ve sade. */
  ustDuzey?: boolean;
  children: React.ReactNode;
}) {
  const notReady = phase !== undefined && phase > CURRENT_PHASE;

  /*
   * Aktif satır dolu mavi bir hap değil; sessiz bir zemin + koyu metin +
   * solda ince aksan çubuğu. Dolu renk, menüdeki tek bir satırı sayfadaki
   * her şeyden daha baskın hale getiriyordu — oysa menü bir araç, içerik değil.
   * Çubuk, sayfa başlığındaki aksan çubuğuyla aynı dili konuşuyor.
   */
  const className = ustDuzey
    ? cn(
        'flex items-center gap-2.5 rounded-[var(--radius)] border px-3 py-2.5',
        'text-sm tracking-[-0.01em] transition-colors',
        active
          ? 'border-accent/30 bg-accent-soft font-semibold text-accent'
          : notReady
            ? 'cursor-not-allowed border-border bg-bg-elevated text-fg-subtle'
            : 'border-border bg-bg-elevated text-fg hover:border-border-strong hover:bg-bg-sunken',
      )
    : cn(
        /*
         * Alt maddeler kasten SADE: üst düzeyle aynı ağırlıkta çizilirlerse
         * menü on kartlık bir duvara dönüşüyor ve hiyerarşi kayboluyor.
         * Aktif olan solundaki çubukla ayrılıyor.
         */
        'relative flex items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 py-1.5',
        'text-[0.8125rem] transition-colors',
        active
          ? 'bg-accent-soft font-medium text-accent before:absolute before:inset-y-1 ' +
              'before:-left-3 before:w-[2px] before:rounded-full before:bg-accent'
          : notReady
            ? // §19.3 — kontrast ≥ 4.5:1. Pasifliği renkle değil, "F<n>" rozeti ve
              // imleçle anlatıyoruz; soluk gri metin okunmuyordu.
              'cursor-not-allowed text-fg-subtle'
            : 'text-fg-muted hover:bg-bg-sunken hover:text-fg',
      );

  if (notReady) {
    return (
      /*
       * ⚠️ Bu dal şu an ULAŞILAMAZ: navigation.ts'teki en büyük `phase` değeri 3,
       * `CURRENT_PHASE` ise 9 — yani `phase > CURRENT_PHASE` hiçbir zaman doğru
       * olmuyor. Yine de metni kurumsallaştırıldı: ileride bir bölüm kapatılmak
       * istenirse kullanıcıya "F7" gibi iç yol haritası numarası gösterilmesin.
       */
      <span className={className} title="Bu bölüm henüz kullanıma açılmadı" aria-disabled="true">
        <span className="flex-1 truncate">{children}</span>
        <span className="text-[0.625rem] text-fg-subtle">Yakında</span>
      </span>
    );
  }

  return (
    <Link to={to} className={className} onClick={(e) => menuBaglantisiTiklandi(e, to)}>
      {children}
    </Link>
  );
}
